import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'

function formatNumberWithCommas(number: number) {
  const [integerPart, decimalPart] = number.toString().split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger
}

const symbolDecimalsMap: Record<string, number> = {
  SOL: 6,
  WSOL: 6,
  WETH: 6,
  ETH: 6,
  USDT: 4,
  USDC: 4,
  FIAT: 2, // normal number
}

const formatAmountBySymbol = (number: number | string, symbol: string | undefined = 'FIAT') => {
  const decimals = symbolDecimalsMap[symbol?.toUpperCase() ?? '']
  return formatNumberWithCommas(parseAmount(truncateDecimals(number, decimals)))
}

export function parseAmount(amount: string | number): number {
  if (typeof amount === 'string') {
    return parseFloat(amount.replace(/,/g, '')) || 0
  }
  return amount || 0
}

// format usd amount
export const formatUSD = (
  amount: number | string,
  options: { prefix?: string; showApproximateSign?: boolean } = {}
): string => {
  const { prefix = '$', showApproximateSign = true } = options
  const num = parseAmount(amount)
  if (num === 0) return `${prefix}0.00`
  if (num < 0.01) {
    return num ? `<${prefix}0.01` : `${prefix}0`
  }
  return `${showApproximateSign ? '~' : ''}${prefix}${formatAmountBySymbol(num)}`
}

// format token amount with some symbol like USDT, USDC, have a rules for each symbol
export const formatTokenAmount = (number: number | string, symbol: string | undefined) => {
  return formatAmountBySymbol(number, symbol)
}

// use for input element to prevent keyboard issue
export const formatInputValue = (newValue: string, maxDecimals: number) => {
  newValue = newValue.replace(',', '.')
  newValue = newValue.replace(/[^\d.]/g, '')
  const parts = newValue.split('.')
  if (parts.length > 2) {
    newValue = parts[0] + '.' + parts.slice(1).join('')
  }
  newValue = newValue.replace(/^0+(?=\d)/, '')
  if (newValue.startsWith('.')) {
    newValue = '0' + newValue
  }
  const decimalIndex = newValue.indexOf('.')
  if (decimalIndex !== -1 && newValue.length - decimalIndex > maxDecimals + 1) {
    newValue = newValue.slice(0, decimalIndex + maxDecimals + 1)
  }

  const zeroPattern = new RegExp(`^0\\.0{${maxDecimals},}$`)
  if (zeroPattern.test(newValue)) {
    newValue = newValue.slice(0, -1) + '1'
  }
  return newValue
}

// format normal number with suffix like K, M, B, T when number is too large
export function formatNumber(num: number): string {
  const USD = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  const SUFFIXES: [number, string][] = [
    [1e15, 'Q'],
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ]
  for (const [threshold, suffix] of SUFFIXES) {
    if (num >= threshold) {
      return `${USD.format(num / threshold)}${suffix}`
    }
  }
  return USD.format(num)
}

// 1.234 => 3
export function countDecimals(number: any) {
  number = number + ''
  if (Math.floor(number) === number) return 0 // No decimals if it's an integer
  return number.toString().split('.')?.[1]?.length || 0
}

/**
 * Truncates a number to a fixed number of decimal places without rounding.
 * @param input - Input number as string (supports scientific notation).
 * @param decimal - Number of decimal places to keep.
 * @param fallbackValue - The value to return if the input is not a number.
 * @returns The truncated number as a string.
 * @example
 * decimal = 5
 * 1.23456 => 1.2345
 * 1.234 => 1.234
 * 1 => 1
 */
export const truncateDecimals = (number: number | string, decimal = 8, fallbackValue = '0'): string => {
  if (!number || isNaN(Number(number))) return fallbackValue

  const numBig = new BigNumber(number)

  const parts = numBig.toFixed().split('.')
  const integerPart = parts[0]
  let decimalPart = parts.length > 1 ? parts[1] : ''

  if (decimalPart.length > decimal) {
    decimalPart = decimalPart.substring(0, decimal)
  }

  if (decimalPart) {
    return `${integerPart}.${decimalPart}`
  } else {
    return integerPart
  }
}

// same as ethers.parseUnit but truncate decimals to make it not error
export const parseUnits = (value: string, decimals: number) => {
  try {
    return ethers.parseUnits(truncateDecimals(value, decimals), decimals)
  } catch {
    return 0n
  }
}

export const formatUnits = (value: ethers.BigNumberish, decimals: number, withTruncateDecimals = false) => {
  try {
    const val = ethers.formatUnits(value, decimals)
    return withTruncateDecimals ? truncateDecimals(val, decimals) : val
  } catch {
    return '0'
  }
}
